// ADDED: the Collections page. The NavBar linked here and the Prisma schema had the model,
// but there was no page and no backend behind it.
import React, { useState, useEffect, useCallback } from 'react';

import axios, { getErrorMessage } from '../../api/axios';

export default function Collections() {
    const [collections, setCollections] = useState([]);
    const [openCollection, setOpenCollection] = useState(null); // the expanded collection's cars
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    // ADDED: the two counters shown above the page
    const [stats, setStats] = useState(null);

    const [newCollectionName, setNewCollectionName] = useState('');
    const [renamingId, setRenamingId] = useState(null);
    const [renameValue, setRenameValue] = useState('');
    // ADDED: which collection is being exported right now, so its button can show progress
    const [exportingId, setExportingId] = useState(null);

    const loadCollections = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await axios.get('/collections');
            setCollections(response.data.collections);
            setError('');
        } catch (err) {
            setError(getErrorMessage(err, 'Could not load your collections.'));
        } finally {
            setIsLoading(false);
        }
    }, []);

    // ADDED: totals are recalculated server-side, so they are refetched after every change
    const loadStats = useCallback(async () => {
        try {
            const response = await axios.get('/collections/stats');
            setStats(response.data.stats);
        } catch (err) {
            // A failed counter should never block the page itself
            console.error(err);
        }
    }, []);

    useEffect(() => { loadCollections(); loadStats(); }, [loadCollections, loadStats]);

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!newCollectionName.trim()) return;
        try {
            await axios.post('/collections', { collectionName: newCollectionName.trim() });
            setNewCollectionName('');
            loadCollections();
            loadStats(); // ADDED
        } catch (err) {
            setError(getErrorMessage(err, 'Could not create the collection.'));
        }
    };

    const handleRename = async (collectionId) => {
        if (!renameValue.trim()) return;
        try {
            await axios.put(`/collections/${collectionId}`, { collectionName: renameValue.trim() });
            setRenamingId(null);
            loadCollections();
        } catch (err) {
            setError(getErrorMessage(err, 'Could not rename the collection.'));
        }
    };

    // ADDED: the public/private switch. Public means "my friends can see this one" - it is
    // never visible to the internet or to collectors you have not accepted.
    const handleToggleVisibility = async (collection) => {
        const nextValue = !collection.isPublic;
        // Moved locally first so the switch does not lag behind the finger on a phone
        setCollections((previous) => previous.map((item) =>
            item.id === collection.id ? { ...item, isPublic: nextValue } : item));

        try {
            await axios.put(`/collections/${collection.id}/visibility`, { isPublic: nextValue });
        } catch (err) {
            setError(getErrorMessage(err, 'Could not change who can see this collection.'));
            loadCollections(); // put the switch back where the server says it is
        }
    };

    // ADDED: downloads the collection as an Excel file. The server builds the .xlsx; the
    // browser receives it as a blob and saves it under the name the server suggests.
    const handleExport = async (collection) => {
        setExportingId(collection.id);
        try {
            const response = await axios.get(`/collections/${collection.id}/export`, { responseType: 'blob' });
            const match = /filename="([^"]+)"/.exec(response.headers['content-disposition'] || '');
            const url = URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = match ? match[1] : 'collection.xlsx';
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            setError('Could not export this collection. Please try again.');
        } finally {
            setExportingId(null);
        }
    };

    const handleDelete = async (collectionId, name) => {
        // Deleting removes the links, not the shared catalog entries
        if (!window.confirm(`Delete "${name}"? The models stay in the catalog.`)) return;
        try {
            await axios.delete(`/collections/${collectionId}`);
            if (openCollection?.id === collectionId) setOpenCollection(null);
            loadCollections();
            loadStats(); // ADDED
        } catch (err) {
            setError(getErrorMessage(err, 'Could not delete the collection.'));
        }
    };

    // Expanding loads the cars on demand, so the list page stays light
    const handleToggleOpen = async (collectionId) => {
        if (openCollection?.id === collectionId) {
            setOpenCollection(null);
            return;
        }
        try {
            const response = await axios.get(`/collections/${collectionId}`);
            setOpenCollection(response.data.collection);
        } catch (err) {
            setError(getErrorMessage(err, 'Could not open that collection.'));
        }
    };

    const handleRemoveCar = async (collectionId, carId) => {
        try {
            await axios.delete(`/collections/${collectionId}/cars/${carId}`);
            const response = await axios.get(`/collections/${collectionId}`);
            setOpenCollection(response.data.collection);
            loadCollections(); // refresh the count and total on the card
            loadStats(); // ADDED: and the page counters
        } catch (err) {
            setError(getErrorMessage(err, 'Could not remove that model.'));
        }
    };

    // ADDED: sets how many of a casting the user owns here. Passing 0 removes the row,
    // which is what the minus button does when the count is already 1.
    const handleQuantityChange = async (collectionId, carId, nextQuantity) => {
        if (nextQuantity > 999) return;
        try {
            await axios.put(`/collections/${collectionId}/cars/${carId}`, { quantity: nextQuantity });
            const response = await axios.get(`/collections/${collectionId}`);
            setOpenCollection(response.data.collection);
            loadCollections(); // the card's total value and counts both change
            loadStats();
        } catch (err) {
            setError(getErrorMessage(err, 'Could not update the quantity.'));
        }
    };

    const formatPrice = (value) => `$${Number(value || 0).toFixed(2)}`;

    // MODIFIED (styling): hardcoded white/grey/red utilities replaced with the shared
    // theme classes, so the page follows the light/dark toggle like the rest of the app.
    return (
        <section className="cv-page min-h-screen py-8 px-4 sm:px-8">
            <div className="max-w-5xl mx-auto space-y-6">
                <div className="text-center space-y-2">
                    <p className="cv-eyebrow">Your garage</p>
                    <h1 className="cv-display text-4xl sm:text-5xl">Collections</h1>
                    <p className="text-sm cv-muted max-w-xl mx-auto">Group your models however you like and keep an eye on what they are worth.</p>
                </div>

                {/* ADDED: the two counters. totalVehicles counts duplicates, uniqueModels
                    counts each casting once even if it sits in several collections. */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {[
                        { label: 'Cars owned', value: stats?.totalVehicles, hint: 'duplicates included' },
                        { label: 'Unique models', value: stats?.uniqueModels, hint: 'counted once each' },
                        { label: 'Collections', value: stats?.collectionCount, hint: 'in your garage' },
                        // ADDED: sealed packs are reported on their own. The cars inside them
                        // are not catalogued, so they never roll into "Cars owned".
                        { label: 'Multipacks', value: stats?.multipacksOwned, hint: stats?.carsInsidePacks ? `~${stats.carsInsidePacks} cars sealed inside` : 'counted separately', span: 'col-span-2 sm:col-span-1' },
                    ].map((stat) => (
                        <div key={stat.label} className={`cv-card p-4 text-center ${stat.span || ''}`}>
                            <p className="font-display font-bold text-3xl sm:text-4xl cv-accent leading-none">
                                {stat.value ?? '\u2014'}
                            </p>
                            <p className="cv-eyebrow mt-2">{stat.label}</p>
                            <p className="text-xs cv-muted mt-0.5">{stat.hint}</p>
                        </div>
                    ))}
                </div>

                {error && <p className="cv-alert-error text-center">{error}</p>}

                {/* Create form: stacks on phones, inline from sm up */}
                <form onSubmit={handleCreate} className="cv-card p-4 flex flex-col sm:flex-row gap-3">
                    <input
                        type="text"
                        value={newCollectionName}
                        onChange={(e) => setNewCollectionName(e.target.value)}
                        placeholder="Name your new collection"
                        autoComplete="off"
                        className="cv-input flex-1"
                    />
                    <button type="submit" className="cv-btn cv-btn-primary whitespace-nowrap uppercase">
                        Create collection
                    </button>
                </form>

                {isLoading && <p className="text-center cv-muted py-8">Loading your collections...</p>}

                {!isLoading && collections.length === 0 &&
                    <div className="cv-card border-dashed p-8 text-center space-y-2">
                        <p className="cv-strong">You do not have any collections yet.</p>
                        <p className="text-sm cv-muted">Create one above, then add models to it from the Models page.</p>
                    </div>
                }

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {collections.map((collection) => (
                        <div key={collection.id} className="cv-card cv-speedline p-4 space-y-3 h-fit">
                            {renamingId === collection.id ? (
                                <div className="flex flex-col sm:flex-row gap-2">
                                    <input type="text" value={renameValue} autoFocus
                                        onChange={(e) => setRenameValue(e.target.value)}
                                        className="cv-input flex-1 py-1.5" />
                                    <div className="flex gap-2">
                                        <button onClick={() => handleRename(collection.id)}
                                            className="cv-btn cv-btn-primary px-3 py-1.5 text-sm">Save</button>
                                        <button onClick={() => setRenamingId(null)}
                                            className="cv-btn cv-btn-quiet px-3 py-1.5 text-sm">Cancel</button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex justify-between items-start gap-2">
                                    <h2 className="cv-title text-xl break-words">{collection.collectionName}</h2>
                                    <div className="flex gap-1 shrink-0">
                                        <button title="Rename" aria-label={`Rename ${collection.collectionName}`}
                                            onClick={() => { setRenamingId(collection.id); setRenameValue(collection.collectionName); }}
                                            className="p-1.5 rounded-lg cv-muted hover:text-brand-flame transition duration-300">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                                <path d="M2.695 14.763l-1.262 3.154a.5.5 0 00.65.65l3.155-1.262a4 4 0 001.343-.885L17.5 5.5a2.121 2.121 0 00-3-3L3.58 13.42a4 4 0 00-.885 1.343z" />
                                            </svg>
                                        </button>
                                        {/* ADDED: download this collection as an Excel file */}
                                        <button title="Export to Excel" aria-label={`Export ${collection.collectionName} to Excel`}
                                            onClick={() => handleExport(collection)}
                                            disabled={exportingId === collection.id}
                                            className="p-1.5 rounded-lg cv-muted hover:text-brand-flame transition duration-300 disabled:opacity-50 disabled:cursor-wait">
                                            {exportingId === collection.id
                                                ? <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="w-4 h-4 animate-spin">
                                                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
                                                    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                                                  </svg>
                                                : <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                                    <path d="M10.75 2.75a.75.75 0 0 0-1.5 0v8.614L6.295 8.235a.75.75 0 1 0-1.09 1.03l4.25 4.5a.75.75 0 0 0 1.09 0l4.25-4.5a.75.75 0 0 0-1.09-1.03l-2.955 3.129V2.75Z" />
                                                    <path d="M3.5 12.75a.75.75 0 0 0-1.5 0v2.5A2.75 2.75 0 0 0 4.75 18h10.5A2.75 2.75 0 0 0 18 15.25v-2.5a.75.75 0 0 0-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5Z" />
                                                  </svg>}
                                        </button>
                                        <button title="Delete" aria-label={`Delete ${collection.collectionName}`}
                                            onClick={() => handleDelete(collection.id, collection.collectionName)}
                                            className="p-1.5 rounded-lg cv-muted hover:text-brand-flame transition duration-300">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                                <path fillRule="evenodd" d="M8.75 1A2.75 2.75 0 0 0 6 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 1 0 .23 1.482l.149-.022.841 10.518A2.75 2.75 0 0 0 7.596 19h4.807a2.75 2.75 0 0 0 2.742-2.53l.841-10.52.149.023a.75.75 0 0 0 .23-1.482A41.03 41.03 0 0 0 14 4.193V3.75A2.75 2.75 0 0 0 11.25 1h-2.5ZM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4Z" clipRule="evenodd" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* The count and the running total, given a bit of weight */}
                            <div className="flex justify-between items-baseline text-sm">
                                {/* MODIFIED: distinct models and total cars are different numbers once
                                    duplicates are tracked, so the card shows both. */}
                                <span className="cv-muted">
                                    {collection.carCount} {collection.carCount === 1 ? 'model' : 'models'}
                                    {collection.totalUnits > collection.carCount &&
                                        <span className="cv-accent"> &middot; {collection.totalUnits} cars</span>}
                                </span>
                                <span className="font-display font-bold text-lg cv-accent">{formatPrice(collection.collectionTotalValue)}</span>
                            </div>

                            {/* ADDED: who can see this collection. Off by default; on means
                                every collector on your friends list can browse the models
                                in it - not quantities, and not what it is worth. */}
                            <button
                                type="button"
                                onClick={() => handleToggleVisibility(collection)}
                                aria-pressed={Boolean(collection.isPublic)}
                                className="flex items-center justify-between gap-3 w-full rounded-xl px-3 py-2 text-sm transition duration-300"
                                style={{ border: '1px solid var(--border-subtle)' }}>
                                <span className="flex items-center gap-2 min-w-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"
                                         className={`w-4 h-4 shrink-0 ${collection.isPublic ? 'cv-accent' : 'cv-muted'}`}>
                                        {collection.isPublic
                                            ? <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0 0a8.949 8.949 0 0 0 4.951-1.488A3.987 3.987 0 0 0 13 16h-2a3.987 3.987 0 0 0-3.951 3.512A8.949 8.949 0 0 0 12 21Zm3-11a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                                            : <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />}
                                    </svg>
                                    <span className={`truncate ${collection.isPublic ? 'cv-strong' : 'cv-muted'}`}>
                                        {collection.isPublic ? 'Shared with friends' : 'Private'}
                                    </span>
                                </span>
                                {/* A plain switch built from two divs - no new dependency */}
                                <span className="relative w-10 h-5 rounded-full shrink-0 transition duration-300"
                                      style={{ backgroundColor: collection.isPublic ? 'var(--accent)' : 'var(--border-subtle)' }}>
                                    <span className="absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all duration-300"
                                          style={{ left: collection.isPublic ? '1.375rem' : '0.125rem' }} />
                                </span>
                            </button>

                            <button onClick={() => handleToggleOpen(collection.id)}
                                className="cv-btn cv-btn-outline w-full text-sm">
                                {openCollection?.id === collection.id ? 'Hide models' : 'View models'}
                            </button>

                            {openCollection?.id === collection.id &&
                                <div className="pt-3 space-y-2 max-h-80 overflow-y-auto animate-fade-up"
                                     style={{ borderTop: '1px solid var(--border-subtle)' }}>
                                    {openCollection.cars.length === 0 &&
                                        <p className="text-sm cv-muted text-center py-3">No models in here yet. Add some from the Models page.</p>
                                    }
                                    {/* MODIFIED: the chip now carries a quantity counter. It wraps onto a
                                        second line on phones so nothing gets squeezed. */}
                                    {openCollection.cars.map((car) => (
                                        <div key={car.id} className="flex items-center gap-3 rounded-xl p-2 flex-wrap sm:flex-nowrap"
                                             style={{ border: '1px solid var(--border-subtle)' }}>
                                            {/* MODIFIED: the ~20KB preview. Lists no longer receive the full photo, which */}
                                            {/* pushed a page over the database's 5MB response limit. */}
                                            {car.imageThumb
                                                ? <img src={car.imageThumb} alt={car.name} className="w-14 h-14 object-cover rounded-lg shrink-0" />
                                                : <div className="cv-well w-14 h-14 rounded-lg shrink-0" />}
                                            <div className="flex-1 min-w-0">
                                                <p className="cv-strong truncate text-sm">{car.name}</p>
                                                <p className="text-xs cv-muted truncate">{car.brand} {car.model} · {car.datereleased}</p>
                                                {/* The line total, so it is obvious why the collection value moved */}
                                                <p className="text-xs cv-accent mt-0.5">
                                                    {formatPrice(car.price)}
                                                    {car.quantity > 1 && ` × ${car.quantity} = ${formatPrice(car.price * car.quantity)}`}
                                                </p>
                                            </div>

                                            {/* ADDED: the owned-quantity counter */}
                                            <div className="flex items-center gap-1 shrink-0 rounded-lg"
                                                 style={{ border: '1px solid var(--border-subtle)' }}>
                                                <button
                                                    aria-label={`Decrease quantity of ${car.name}`}
                                                    title={car.quantity <= 1 ? 'Remove from this collection' : 'One fewer'}
                                                    onClick={() => handleQuantityChange(collection.id, car.id, car.quantity - 1)}
                                                    className="w-8 h-8 flex items-center justify-center cv-muted hover:text-brand-flame transition duration-300">
                                                    &minus;
                                                </button>
                                                <span className="font-display font-bold text-sm cv-strong w-6 text-center tabular-nums">
                                                    {car.quantity}
                                                </span>
                                                <button
                                                    aria-label={`Increase quantity of ${car.name}`}
                                                    title="One more"
                                                    onClick={() => handleQuantityChange(collection.id, car.id, car.quantity + 1)}
                                                    className="w-8 h-8 flex items-center justify-center cv-muted hover:text-brand-flame transition duration-300">
                                                    +
                                                </button>
                                            </div>

                                            <button title="Remove from collection"
                                                aria-label={`Remove ${car.name} from this collection`}
                                                onClick={() => handleRemoveCar(collection.id, car.id)}
                                                className="cv-muted hover:text-brand-flame shrink-0 transition duration-300">
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM6.75 9.25a.75.75 0 0 0 0 1.5h6.5a.75.75 0 0 0 0-1.5h-6.5Z" clipRule="evenodd" />
                                                </svg>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            }
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
